import PropTypes from 'prop-types';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

import { Box, Typography } from '@mui/material';

import { Menu } from './Menu';

const useStyles = makeStyles()((theme) => ({
    root: {
        padding: 60,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#F9F9F9',
        color: '#282D31',
        '& a': {
            color: '#282D31'
        }
    },
    box: {
        maxWidth: 1080,
        margin: '0 auto'
    },
    logo: {
        paddingBottom: 60,
        height: 24,
        marginRight: 200,
        '@media (max-width: 960px)': {
            height: 31,
            marginRight: 0,
            '& img': {
                margin: '0 auto'
            }
        }
    },
    body: {
        display: 'flex',
        flexWrap: 'wrap',
        maxWidth: 1024,
        '@media (max-width: 960px)': {
            display: 'block',
            textAlign: 'center'
        }
    },
    copyright: {
        fontSize: 12,
        marginTop: 60,
        color: theme.palette.secondary.dark
    },
    tos: {
        color: theme.palette.secondary.dark,
        '& a': {
            color: theme.palette.secondary.dark
        }
    }
}));

export const FooterBar = ({ customClasses }) => {
    const { classes, cx } = useStyles();
    const handleOpenZendeskForm = (event) => {
        event.preventDefault();
        if (window.zE) {
            window.zE('webWidget', 'open');
        }
    };

    return (
        <footer className={cx(classes.root, customClasses.footerRoot)}>
            <div className={classes.box}>
                <Box className={classes.body}>
                    <img
                        className={cx(classes.logo, customClasses.footerLogo)}
                        alt="vulners-logo-small"
                        src={'/static/img/icons/logo.svg'}
                    />
                    <Menu data={menus} />
                </Box>
                <Typography variant="caption" className={classes.tos}>
                    This site is protected by reCAPTCHA and the Google{' '}
                    <a href="https://policies.google.com/privacy">Privacy Policy</a> and{' '}
                    <a href="https://policies.google.com/terms">Terms of Service</a> apply. All product names, logos,
                    and brands are property of their respective owners. All company, product and service names used in
                    this website are for identification purposes only. Use of these names, logos, and brands does not
                    imply endorsement.If you are an owner of some content and want it to be removed,
                    <a href="/" onClick={handleOpenZendeskForm}>
                        {' '}
                        please contact us
                    </a>
                    . Using Vulners services you are accepting{' '}
                    <a target="_blank" href="/static/docs/eula.pdf">
                        Vulners services end-user license agreement
                    </a>
                </Typography>

                <Typography className={classes.copyright}>@{new Date().getFullYear()} Vulners Inc</Typography>
            </div>
        </footer>
    );
};

FooterBar.defaultProps = { customClasses: {} };

FooterBar.propTypes = {
    customClasses: PropTypes.shape({
        footerRoot: PropTypes.string,
        footerLogo: PropTypes.string
    })
};

export const menus = [
    {
        title: 'Products',
        items: [
            {
                label: 'Security Intelligence',
                url: '/feed'
            },
            {
                label: 'Non-intrusive assessment',
                url: '/assessment'
            },
            {
                label: 'Developers SDK',
                url: '/sdk'
            },
            {
                label: 'Windows scanner',
                url: '/windows'
            },
            {
                label: 'Linux scanner',
                url: '/linux'
            },
            {
                label: 'Perimeter control tool',
                url: '/perimeter-control'
            }
        ]
    },
    {
        title: 'Database',
        items: [
            {
                label: 'Vulnerabilities',
                url: '/search?query=viewCount:[50%20TO%20*]%20order:viewCount%20last%207%20days'
            },
            {
                label: 'Exploits',
                url: '/search?query=bulletinFamily:exploit%20order:published'
            },
            {
                label: 'IOC',
                url: '/search?query=bulletinFamily:ioc%20order:published'
            },
            {
                label: 'Security News',
                url: '/search?query=bulletinFamily:info'
            },
            {
                label: 'BugBounty',
                url: '/search?query=bulletinFamily:bugbounty%20order:published'
            },
            {
                label: 'Popular',
                url: '/search?query=enchantments.twitter.counter:[1%20TO%20*]%20order:enchantments.twitter.counter'
            },
            {
                label: 'Wild Exploited',
                url: '/search?query=enchantments.exploitation.wildExploited:true'
            },
            {
                label: 'Top Vulnerabilities',
                url: '/sitemap'
            }
        ]
    },
    {
        title: 'Tools',
        items: [
            {
                label: 'Linux Security Scanner',
                url: '/linux-scanner/scan'
            },
            {
                label: 'API integration',
                url: '/apiscan-help'
            },
            {
                label: 'Subscriptions',
                url: '/email'
            },
            {
                label: 'Plugins',
                url: '/products'
            },
            {
                label: 'Manual Audit',
                url: '/audit'
            }
        ]
    },
    {
        title: 'Learn More',
        items: [
            {
                label: 'Stats',
                url: '/stats'
            },
            {
                label: 'API',
                url: '/docs/API_wrapper/api/'
            },
            {
                label: 'Docs',
                url: '/docs'
            },
            {
                label: 'Api-keys',
                url: '/docs/apikey/'
            },
            {
                label: 'License',
                url: '/static/docs/eula.pdf'
            },
            {
                label: 'Pricing',
                url: '/pricing'
            },
            {
                label: 'Glossary',
                url: 'https://vulners.com/glossary'
            },
            {
                label: 'FAQ',
                url: 'https://vulners.com/faq'
            }
        ]
    },
    {
        title: 'Company',
        items: [
            {
                label: 'Blog',
                url: 'https://vulners.com/blog'
            },
            {
                label: 'Contacts',
                url: '/company/contacts'
            },
            {
                label: 'About Us',
                url: '/company/about-us'
            },
            {
                label: 'OpenSource',
                url: 'https://github.com/vulnerscom'
            },
            {
                label: 'EULA',
                url: '/static/docs/eula.pdf'
            },
            {
                label: 'Brand Guideline',
                url: '/company/branding'
            },
            {
                label: 'Privacy Policy',
                url: '/static/docs/privacy_policy.html',
                target: '_blank'
            }
        ]
    }
];
